import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';

import { useOrdersAnalytics } from '../../features/orders/useOrders';
import { RANGES, rollup, windowFor } from '../../helpers/ordersAnalytics';
import { formatLength } from '../../helpers/formatLength';
import OrdersAnalyticsKpis from '../../components/sales-management/analytics/OrdersAnalyticsKpis';
import MetreFateChart from '../../components/sales-management/analytics/MetreFateChart';
import OrderVolumeChart from '../../components/sales-management/analytics/OrderVolumeChart';
import TopClientsChart from '../../components/sales-management/analytics/TopClientsChart';
import OrdersAnalyticsTable from '../../components/tables/OrdersAnalyticsTable';

/**
 * The order book, all of it.
 *
 * Every other sales screen shows the orders that are still open, because those
 * are the ones somebody has to do something about. That leaves the factory with
 * no memory: a delivered order vanishes into an archive collection, a deleted
 * one into a different collection again, and the question "how much did we sell
 * last quarter, and how much of it did we actually make" had no screen at all.
 *
 * Built around metres rather than orders. An order is a piece of paper; a metre
 * of cable is machine time, copper and a delivery date, and it is the unit every
 * decision downstream of this page is denominated in.
 */

const OrdersAnalytics = () => {
  const [range, setRange] = useState('12m');

  const params = useMemo(() => windowFor(range), [range]);
  const { data, isLoading, isError, error, isFetching, refetch } =
    useOrdersAnalytics(params);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const { kpis, fate, byMonth, byClient } = useMemo(
    () => rollup(rows, { locale: 'ro-RO' }),
    [rows]
  );

  return (
    <div className="pb-9">
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="mb-1">Orders analytics</h2>
          <p className="text-700 mb-0">
            Open, delivered and deleted orders together — what was sold, what was
            made, and what is still only a promise.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          {RANGES.map(r => (
            <Button
              key={r.key}
              size="sm"
              variant={range === r.key ? 'primary' : 'phoenix-secondary'}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </Button>
          ))}
          <Button
            size="sm"
            variant="phoenix-secondary"
            onClick={() => refetch()}
            disabled={isFetching}
            title="Reload"
          >
            <FontAwesomeIcon icon={faRotate} spin={isFetching} />
          </Button>
        </div>
      </div>

      {isError && (
        <Alert variant="danger">
          {error?.response?.data?.error ||
            error?.message ||
            'Could not load the order history'}
        </Alert>
      )}

      {isLoading ? (
        <div className="d-flex justify-content-center py-9">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <OrdersAnalyticsKpis kpis={kpis} />

          <Row className="g-3 mb-3">
            <Col xs={12} xl={8}>
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="text-1100 mb-0">Order book by month</h4>
                    <Badge bg="secondary" variant="phoenix" className="fs-10">
                      {formatLength(kpis.ordered)} total
                    </Badge>
                  </div>
                  <p className="text-700 fs-9 mb-3">
                    Bar height is what was sold that month; the colour is how far
                    it got.
                  </p>
                  <OrderVolumeChart
                    months={byMonth}
                    style={{ height: 320, width: '100%' }}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={4}>
              <Card className="h-100">
                <Card.Body>
                  <h4 className="text-1100 mb-1">Where the metres went</h4>
                  <p className="text-700 fs-9 mb-3">
                    Each metre counted once, by how final its fate is.
                  </p>
                  <MetreFateChart
                    fate={fate}
                    style={{ height: 320, width: '100%' }}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xs={12} xl={4}>
              <Card className="h-100">
                <Card.Body>
                  <h4 className="text-1100 mb-1">Top customers</h4>
                  <p className="text-700 fs-9 mb-3">
                    By metres ordered, over the whole period.
                  </p>
                  <TopClientsChart
                    clients={byClient}
                    style={{ height: 300, width: '100%' }}
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} xl={8}>
              <Card className="h-100">
                <Card.Body>
                  <OrdersAnalyticsTable rows={rows} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default OrdersAnalytics;
